import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ErrorLogoSVG } from '@shared-assets/images';
import { BannerService } from '@terminus-ui/banner';
import { LinkComponent } from '@terminus-ui/link';
import {
  PageHeaderButtonsComponent,
  PageHeaderComponent,
} from '@terminus-ui/page-header';
import { SvgComponent } from '@terminus-ui/svg';
import { applicationManagerMessages } from 'grpc-gateway-generated';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { ApplicationManagerApiService } from '../application-manager-api.service';
import {
  INSTANCE_TYPES,
  IntentTopicLimitOption,
  INTENT_TOPIC_LIMIT_OPTIONS,
} from '../customer-configuration/app-instance.config';
import { CustomerDialogComponent } from '../customer-dialog/customer-dialog.component';
import { CustomerService } from '../customer.service';
import { countryCodes } from './country.codes';

@Component({
  selector: 'lib-customer-configuration',
  templateUrl: './customer-configuration.component.html',
  imports: [
    PageHeaderComponent,
    PageHeaderButtonsComponent,
    ButtonModule,
    CommonModule,
    DialogModule,
    SelectModule,
    ToggleSwitchModule,
    InputTextModule,
    ReactiveFormsModule,
    TableModule,
    SvgComponent,
    ProgressSpinnerModule,
    LinkComponent,
    RouterLink,
  ],
  styles: [
    '.width-for-parent-table {width: 400px;}',
    '.special-width-for-child-table {width: 380px}',
    `
      .empty-table-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 400px;
      }
    `,
  ],
  providers: [DialogService],
})
export class CustomerConfigurationComponent implements OnInit {
  /** control for app instance modal */
  addAppInstanceVisible = false;
  instanceTypes = [...INSTANCE_TYPES];
  intentTopicLimitOptions: IntentTopicLimitOption[] = [
    ...INTENT_TOPIC_LIMIT_OPTIONS,
  ];
  appInstanceForm = new FormGroup({
    instanceType: new FormControl('', Validators.required),
    instanceName: new FormControl('', Validators.required),
    instanceParentId: new FormControl(''),
    dataStudioInstanceForm: new FormGroup({
      isListBased: new FormControl(false),
      businessUnit: new FormControl(''),
      intentTopicLimit: new FormControl(0), //Apps-2544
    }),
    adexInstanceForm: new FormGroup({
      country_code: new FormControl('US'),
    }),
    advertiserInstanceForm: new FormGroup({
      advertiserId: new FormControl(''),
    }),
  });
  private applicationManagerApi = inject(ApplicationManagerApiService);
  private bannerService = inject(BannerService);
  private customerService = inject(CustomerService);
  private activatedRoute = inject(ActivatedRoute);
  private dialogService = inject(DialogService);

  errorLogo = ErrorLogoSVG;
  countryCodes = countryCodes;
  expandedInstances: Record<string, { [key: string]: boolean }> = {};

  private customerID$ = new BehaviorSubject<string>('');
  private refreshCustomerData$ = new Subject<boolean>();
  customerData$ = combineLatest([
    this.customerID$.pipe(filter(Boolean)),
    this.refreshCustomerData$.pipe(startWith(false)),
  ]).pipe(
    switchMap(([customerId, refresh]) =>
      this.customerService.getCustomerById(customerId, refresh),
    ),
    filter(Boolean),
    shareReplay(1),
  );

  private refreshInstances$ = new Subject<boolean>();
  customerAppInstances$ = combineLatest([
    this.customerData$,
    this.refreshInstances$.pipe(startWith(false)),
  ]).pipe(
    switchMap(([customer, refresh]) =>
      this.customerService.getCustomerAppInstances(
        customer?.customerId || '',
        refresh,
      ),
    ),
    filter(Boolean),
    shareReplay(1),
  );

  sortedStackedCustomerAppInstances$ = this.customerAppInstances$.pipe(
    map((instances) => {
      let parentInstances: Array<
        applicationManagerMessages.Instance & {
          children?: applicationManagerMessages.Instance[];
        }
      > = [];

      /** we're adding a children array to each parent level, if we're a child then we get processed after we make the parent array. */
      parentInstances = [...instances].filter((instance) => !instance.parentId);
      /** we're adding a resetting the children to zero, for some reason the ref from the service keeps getting passed in. */
      parentInstances = parentInstances.map((instance) => {
        instance.children = [];
        return instance;
      });

      instances.forEach((instance) => {
        if (instance.parentId) {
          const parent = parentInstances.find(
            (parent) => parent.instanceId === instance.parentId,
          );
          if (parent) {
            if (!parent.children) parent.children = [];
            parent.children.push(instance);
          }
        }
      });

      return parentInstances;
    }),
    tap((parentInstances) => {
      this.expandedInstances = {};
      parentInstances.forEach((parent) => {
        this.expandedInstances[parent.instanceId || ''] = {};
        this.expandedInstances[parent.instanceId || ''][
          parent.instanceId || ''
        ] = true;
      });
    }),
  );

  customerAppAlignmentOptions$ = this.customerAppInstances$.pipe(
    map((instances) =>
      instances.filter(
        (instance) => !instance.parentId && instance.type === 'DATA_STUDIO',
      ),
    ),
  );

  ngOnInit(): void {
    firstValueFrom(this.activatedRoute.paramMap).then((paramMap) => {
      if (paramMap.has('id')) this.customerID$.next(paramMap.get('id') || '');
    });
  }

  addAppInstance() {
    let appInstanceRequest: applicationManagerMessages.ProvisionInstanceRequest;

    switch (this.appInstanceForm.controls.instanceType.value) {
      case 'DATA_STUDIO': {
        appInstanceRequest = {
          customerId: this.customerID$.value,
          type: this.appInstanceForm.get('instanceType')?.value || '',
          name: this.appInstanceForm.get('instanceName')?.value || '',
          parentId: this.appInstanceForm.get('instanceParentId')?.value || '',
          dataStudioData: this.appInstanceForm.get('dataStudioInstanceForm')
            ?.value as applicationManagerMessages.DataStudioProvisionData,
        };
        break;
      }
      case 'ADX': {
        appInstanceRequest = {
          customerId: this.customerID$.value,
          type: this.appInstanceForm.get('instanceType')?.value || '',
          name: this.appInstanceForm.get('instanceName')?.value || '',
          parentId: this.appInstanceForm.get('instanceParentId')?.value || '',
          adXData: this.appInstanceForm.get('adexInstanceForm')
            ?.value as applicationManagerMessages.AdXProvisionData,
        };
        break;
      }
      case 'ADVERTISER': {
        appInstanceRequest = {
          customerId: this.customerID$.value,
          type: this.appInstanceForm.get('instanceType')?.value || '',
          name: this.appInstanceForm.get('instanceName')?.value || '',
          parentId: this.appInstanceForm.get('instanceParentId')?.value || '',
          advertiserData: this.appInstanceForm.get('advertiserInstanceForm')
            ?.value as applicationManagerMessages.AdvertiserProvisionData,
        };
        break;
      }
      default: {
        appInstanceRequest = {
          customerId: this.customerID$.value,
          type: this.appInstanceForm.get('instanceType')?.value || '',
          name: this.appInstanceForm.get('instanceName')?.value || '',
          parentId: this.appInstanceForm.get('instanceParentId')?.value || '',
        };
        break;
      }
    }

    this.bannerService.pushBanner({
      isClosable: true,
      isFloating: false,
      title: `Provisioning a new app instance.`,
      type: 'success',
      enableAutoDismissWithDuration: 2000,
    });

    firstValueFrom(
      this.applicationManagerApi.createCustomerAppInstance(appInstanceRequest),
    )
      .then(() => {
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Successfully provisioned a new app instance.`,
          type: 'success',
          enableAutoDismissWithDuration: 10000,
        });
        this.appInstanceForm.reset();
        this.addAppInstanceVisible = false;
        this.refreshInstances$.next(true);
      })
      .catch((err) => {
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Unable to provision a new app instance: ${err}`,
          type: 'warning',
          enableAutoDismissWithDuration: 10000,
        });
        console.error('Unable to provision app instance:', err);
      });
  }

  editCustomer() {
    this.dialogService
      .open(CustomerDialogComponent, {
        header: 'Edit Customer',
        width: '35rem',
        style: { 'min-height': 'fit-content' },
        contentStyle: { overflow: 'visible' },
        data: { customer: this.customerData$ },
      })
      .onClose.subscribe((closeValue) => {
        if (closeValue) this.refreshCustomerData$.next(true);
      });
  }
}
